import * as psl from 'psl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useBulkCapabilityJob } from '@/hooks/useCapability';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { isSeedDomain, isSeedIP, Seed } from '@/sections/seeds/types';
import { ResourceType } from '@/types';
import { pluralize } from '@/utils/pluralize.util';

/**
 * Return type for useRefreshSeedData hook
 */
export interface RefreshSeedDataResult {
  /**
   * Re-run WHOIS enrichment for the domain and IP/CIDR seeds in the list
   * (no-op if the list contains none of those types).
   */
  refresh: (seeds: Seed[]) => Promise<void>;
  /** True from click until the final poll fires (drives inline spinners) */
  isRefreshing: boolean;
}

/**
 * Resolve the WHOIS file key for a seed exactly the way the drawer does.
 *
 * Mirrors the post-PR-#6549 `useSeedDrawerData` key derivation and MUST stay in
 * sync with it: domain seeds collapse to their eTLD+1 on write, so we key on
 * `psl.get(identifier) || identifier`; every other seed (IP/CIDR) is written
 * under its RAW identifier verbatim (including `a.b.c.d/nn` CIDR keys), so we
 * use `identifier` unchanged. This keeps the invalidated query keys aligned
 * with the drawer's `useGetFile` queries.
 */
const whoisFileKeyFor = (seed: Seed): string =>
  isSeedDomain(seed)
    ? psl.get(seed.identifier) || seed.identifier
    : seed.identifier;

/**
 * Poll schedule (ms after the job fires) for re-checking the WHOIS files.
 * WHOIS re-enrichment runs RDAP/Whoxy on the worker plane and writes new files
 * a short while later, so we invalidate immediately and again on this schedule
 * to surface the fresh data without a manual reload.
 */
const POLL_DELAYS_MS = [6000, 18000, 36000] as const;

/**
 * Shared "Refresh Data" hook — re-runs WHOIS enrichment for domain and IP/CIDR
 * seeds.
 *
 * Single source of truth for firing the data-refresh jobs: both the seeds Scan
 * dropdown and the drawer blank-state Refresh buttons call this. Domain seeds
 * fire the `whois` capability; IP/CIDR seeds fire the `ip` capability. A manual
 * `POST /job` is force-stamped server-side (AllowRepeat + Full), so it re-runs
 * fresh — the documented recovery lever for blank Domain Details / Domain
 * History / IP details.
 *
 * After firing, it invalidates the `whois/<key>` and `whois-history/<key>` file
 * queries immediately and again on a short schedule so the drawer updates on
 * its own. It also invalidates the seed-rows (`MY`/`seed`) query on the same
 * schedule, because the same capabilities re-stamp persisted Seed fields shown
 * as table columns — so the table refetches its fresh values in lockstep with
 * the files.
 */
export const useRefreshSeedData = (): RefreshSeedDataResult => {
  const { mutateAsync: runJobs } = useBulkCapabilityJob();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Follow-up invalidation ticks. Deliberately NOT cleared on unmount: once the
  // bulk POST resolves the job has run server-side, so the freshly written WHOIS
  // files and re-stamped seed columns must still land in the cache even if the
  // drawer/toolbar closed. queryClient is a module singleton and
  // invalidateQueries carries no component state, so these fire safely post-unmount.
  const invalidationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Spinner lifecycle is mount-scoped: this timer owns the setState that clears
  // the inline spinner, so it is the only one cleared on unmount / re-entry.
  const spinnerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard against setState after the drawer/toolbar unmounts mid-refresh.
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Clear only the spinner timer — the invalidation ticks must survive
      // unmount so a refresh queued right before close still updates the cache.
      if (spinnerTimeoutRef.current) clearTimeout(spinnerTimeoutRef.current);
    };
  }, []);

  const invalidateWhois = useCallback((keys: string[]) => {
    keys.forEach(key => {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: `whois/${key}` }),
      });
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: `whois-history/${key}` }),
      });
    });
  }, []);

  // The `whois`/`ip` capabilities also stamp persisted Seed fields (asname,
  // registrant, country, registrar, customer, …) that render as table columns,
  // so the seed rows go stale too. Invalidate by prefix — a single-arg
  // `getMy(Seed)` is exactly `['MY','seed']`, which TanStack prefix-matches
  // every seed-table variant (cursor pagination, impersonation suffixes). This
  // mirrors `useBulkCapabilityJob`'s `getMy(ResourceType.Job)` invalidation.
  //
  // Exclude `useSeeds`' empty-state `allQuery` — the only seed query whose
  // serialized-query segment (queryKey[2] = JSON.stringify(query); positions are
  // stable, useGetUserKey only appends) carries a top-level `limit` and no
  // `orderBy`. This is a deliberate cross-file contract: `useSeeds` builds that
  // probe with `limit: limit ?? 1` precisely so it is self-identifying here (the
  // filtered table query carries no `limit`; the drawer + unsorted table queries
  // carry no `orderBy`, so neither `limit` nor `orderBy` alone would suffice).
  // allQuery exists solely to drive `isAllEmpty`, and a data-refresh re-stamps
  // existing rows without changing the seed count, so its emptiness verdict
  // cannot move — refetching it on every tick is pure waste. The predicate
  // identifies allQuery POSITIVELY and narrowly; any seed query it can't
  // confidently classify is still invalidated, so a future key-shape change
  // degrades to a harmless over-fetch, never a silently stale table.
  const invalidateSeeds = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getQueryKey.getMy(ResourceType.Seed),
      predicate: query => {
        const serialized = query.queryKey[2];
        if (typeof serialized !== 'string') return true;
        let parsed: { limit?: unknown; orderBy?: unknown };
        try {
          parsed = JSON.parse(serialized);
        } catch {
          return true;
        }
        const isEmptyStateQuery =
          parsed?.limit != null && parsed?.orderBy == null;
        return !isEmptyStateQuery;
      },
    });
  }, []);

  const refresh = useCallback(
    async (seeds: Seed[]) => {
      const domainSeeds = seeds.filter(isSeedDomain);
      const ipSeeds = seeds.filter(isSeedIP);
      // Web-application / preseed seeds have no forced-refresh capability.
      if (domainSeeds.length === 0 && ipSeeds.length === 0) return;

      // Cancel the prior refresh's pending polls + spinner flip so an older
      // run's final (36s) tick can't clear THIS refresh's spinner and timers
      // don't accumulate on rapid re-clicks. (Stale invalidation ticks are
      // harmless if they keep firing; we replace them only to avoid buildup.)
      invalidationTimeoutsRef.current.forEach(clearTimeout);
      invalidationTimeoutsRef.current = [];
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current);
        spinnerTimeoutRef.current = null;
      }

      setIsRefreshing(true);

      try {
        // One bulk call with the class-appropriate capability per seed: domains
        // → `whois`, IP/CIDR → `ip`. config stays {} — the backend force-stamps
        // a manual POST /job (AllowRepeat + Full), so no force flags belong here.
        await runJobs([
          ...domainSeeds.map(s => ({
            assetKey: s.key,
            capabilities: ['whois'],
            config: {},
          })),
          ...ipSeeds.map(s => ({
            assetKey: s.key,
            capabilities: ['ip'],
            config: {},
          })),
        ]);

        // The bulk POST resolved, so at least one job was queued server-side
        // (useBulkCapabilityJob rejects only when EVERY job failed). The
        // component may have unmounted while runJobs was in flight — but the
        // invalidations below MUST still run: the data is being re-stamped
        // regardless of whether the drawer/toolbar is still open. Only the React
        // state (spinner) below is mount-guarded.

        const refreshedCount = domainSeeds.length + ipSeeds.length;
        // Fire regardless of mount: the queue succeeded, so this confirmation is
        // still accurate, and sonner renders app-globally (not inside the drawer).
        toast.info(
          `WHOIS refresh queued for ${refreshedCount} ${pluralize(
            refreshedCount,
            'seed'
          )} — updated data appears shortly.`
        );

        const keys = Array.from(
          new Set([...domainSeeds, ...ipSeeds].map(whoisFileKeyFor))
        );

        // Invalidate immediately, then re-check on a short schedule so the
        // drawer surfaces the freshly written WHOIS files — and the table its
        // freshly stamped seed columns — without a reload. These ticks survive
        // unmount by design (see invalidationTimeoutsRef).
        invalidateWhois(keys);
        invalidateSeeds();
        POLL_DELAYS_MS.forEach(delay => {
          invalidationTimeoutsRef.current.push(
            setTimeout(() => {
              invalidateWhois(keys);
              invalidateSeeds();
            }, delay)
          );
        });

        // Spinner lifecycle stays mount-scoped: clear the inline spinner after
        // the final poll, but only while still mounted (and only for the current
        // refresh — a newer one has already replaced this timer above).
        if (mountedRef.current) {
          spinnerTimeoutRef.current = setTimeout(
            () => {
              if (mountedRef.current) setIsRefreshing(false);
            },
            POLL_DELAYS_MS[POLL_DELAYS_MS.length - 1]
          );
        }
      } catch {
        // useBulkCapabilityJob surfaces its own error toast; just stop the spinner.
        if (mountedRef.current) {
          setIsRefreshing(false);
        }
      }
    },
    [runJobs, invalidateWhois, invalidateSeeds]
  );

  return { refresh, isRefreshing };
};
